import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users2,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { groupsApi } from '@/api/endpoints/groups';
import { ApiError } from '@/api/client';
import { cn } from '@/lib/utils';
import type { Group } from '@/api/types';

export function ClientsPage() {
  // Sem `all=true`: o Traccar retorna 400 sem privilégio admin/manager.
  const list = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupsApi.list(),
  });
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter((g) => g.name?.toLowerCase().includes(q));
  }, [list.data, search]);

  const groupsById = useMemo(() => {
    const m = new Map<number, Group>();
    for (const g of list.data ?? []) m.set(g.id, g);
    return m;
  }, [list.data]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/settings"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Clientes</h1>
          <p className="text-sm text-muted-foreground">
            Agrupamentos de máquinas (clientes ou empresas). Permite hierarquia.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Novo cliente
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar cliente"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando clientes…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">
          Erro ao carregar clientes (precisa permissão de admin/manager).
        </p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Users2 className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {search ? 'Nenhum cliente corresponde à busca.' : 'Nenhum cliente cadastrado.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <ClientCard
              key={g.id}
              group={g}
              parent={g.groupId ? groupsById.get(g.groupId)?.name : undefined}
              onEdit={() => setEditing(g)}
            />
          ))}
        </div>
      )}

      {editing && (
        <ClientEditor
          initial={editing === 'new' ? null : editing}
          allGroups={list.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ClientCard({
  group,
  parent,
  onEdit,
}: {
  group: Group;
  parent?: string;
  onEdit: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Users2 className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{group.name}</p>
              {parent ? (
                <p className="truncate text-xs text-muted-foreground">Sob: {parent}</p>
              ) : (
                <p className="truncate text-xs text-muted-foreground">Cliente raiz</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

interface FormState {
  name: string;
  groupId: number | null;
}

function ClientEditor({
  initial,
  allGroups,
  onClose,
}: {
  initial: Group | null;
  allGroups: Group[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({
    name: initial?.name ?? '',
    groupId: initial?.groupId ?? null,
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initial) setForm({ name: initial.name, groupId: initial.groupId ?? null });
  }, [initial]);

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  // Pra evitar ciclo: ao editar, não permitir selecionar a si mesmo nem descendentes como pai.
  const validParentOptions = useMemo(() => {
    if (!initial) return allGroups;
    const banned = new Set<number>([initial.id]);
    let added = true;
    while (added) {
      added = false;
      for (const g of allGroups) {
        if (g.groupId && banned.has(g.groupId) && !banned.has(g.id)) {
          banned.add(g.id);
          added = true;
        }
      }
    }
    return allGroups.filter((g) => !banned.has(g.id));
  }, [allGroups, initial]);

  const buildPayload = (): Group =>
    ({
      ...(initial ?? { id: 0, name: '' }),
      name: form.name.trim(),
      groupId: form.groupId ?? undefined,
    }) as Group;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = buildPayload();
      if (initial) return groupsApi.update(initial.id, payload);
      const { id, ...rest } = payload;
      void id;
      return groupsApi.create(rest);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => groupsApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      onClose();
    },
  });

  const errorText = (e: unknown) => {
    if (e instanceof ApiError) return `Servidor respondeu ${e.status} (${e.statusText || 'erro'}).`;
    if (e instanceof Error) return e.message;
    return 'Erro desconhecido.';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!initial) return;
    if (!window.confirm(`Excluir o cliente "${initial.name}"? Essa ação não pode ser desfeita.`)) return;
    deleteMutation.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">{initial ? 'Editar cliente' : 'Novo cliente'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-3 p-4">
          <Field label="Nome" required error={touched ? errors.name : undefined}>
            <Input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
            />
          </Field>
          <Field label="Cliente pai (opcional)">
            <select
              value={form.groupId === null ? '' : String(form.groupId)}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  groupId: e.target.value ? Number(e.target.value) : null,
                }))
              }
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Nenhum (raiz)</option>
              {validParentOptions
                .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </select>
          </Field>

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={onConfirmDelete}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}
